import React, { useCallback, useContext, useMemo } from 'react';
import { Confirm } from 'wix-ui-icons-common';
import { OCCUPIED_SEAT_CIRCLE_DIAMETER, OCCUPIED_SEAT_FILL, SEAT_CIRCLE_DIAMETER, SHAPE_STROKE_COLOR, SeatingViewerContext, isPlaceOccupied, radiansToDegrees, reverseDegrees, ComponentContext, COLORS, SeatTypeImage, isPlaceSeatTypeImage, WholeElementSelectionMode, ReverseRotate, } from '@wix/seating-common-components';
import classNames from 'classnames';
import { isDarkColor } from '@wix/panda-js-utils';
import { getVisualState } from '../../utils';
import s from './seat.scss';
export const Seat = ({ definition, definition: { shapeColor, shapeTextColor, places, categoryId, angle }, x = 0, y = 0, label, realAngle, }) => {
    const { currentlyClickedPlaceId, selectedPlaceIds, placesStock, mobile, onPlaceClick, onPlaceMouseEnter, onPlaceMouseLeave, seatConfig, wholeElementSelectionMode, } = useContext(SeatingViewerContext);
    const { backgroundColor } = useContext(ComponentContext);
    const place = places.find(item => item.label === label);
    const seatType = place?.type;
    const fillColor = shapeColor ?? place.seatColor;
    const textColor = shapeTextColor ?? place.seatTextColor;
    const hasCategory = definition.places?.find(({ label: placeLabel }) => placeLabel === label)?.categoryId != null;
    const wholeElementReservation = definition.reservationOptions?.reserveWholeElement;
    const enableWholeElementBehavior = wholeElementReservation && wholeElementSelectionMode === WholeElementSelectionMode.WHOLE_ONLY;
    const isDarkBackground = isDarkColor(backgroundColor);
    const placeDetails = useMemo(() => ({ label: definition.label, sectorLabel: definition.sectorLabel }), [definition.label, definition.sectorLabel]);
    const onPlaceEnter = useCallback((event) => onPlaceMouseEnter && onPlaceMouseEnter({ event, place, categoryId, details: placeDetails }), [place, categoryId, onPlaceMouseEnter, placeDetails]);
    const onPlaceLeave = useCallback((event) => onPlaceMouseLeave && onPlaceMouseLeave({ event, place, categoryId, details: placeDetails }), [place, categoryId, onPlaceMouseLeave, placeDetails]);
    const handlePlaceClick = useCallback((event) => {
        event.stopPropagation();
        onPlaceClick?.({ place, categoryId });
    }, [place, categoryId, onPlaceClick]);
    const placeId = place.id;
    const clickedPlace = enableWholeElementBehavior
        ? places.some(({ id }) => currentlyClickedPlaceId === id)
        : currentlyClickedPlaceId === placeId;
    const selected = enableWholeElementBehavior
        ? places.some(({ id }) => selectedPlaceIds.includes(id))
        : selectedPlaceIds.includes(placeId);
    const occupied = wholeElementReservation
        ? places.some(_place => isPlaceOccupied(_place, placesStock))
        : isPlaceOccupied(place, placesStock);
    const selectedOrClicked = clickedPlace || selected;
    const radius = SEAT_CIRCLE_DIAMETER / 2;
    const centerX = x + radius;
    const centerY = y + radius;
    const resolvedAngle = realAngle ?? angle;
    const rotatedDegrees = radiansToDegrees(resolvedAngle);
    const reversedRotatedDegrees = reverseDegrees(rotatedDegrees);
    const { disableSeatTypeImages, visualStates } = seatConfig ?? {};
    const state = getVisualState({ selected, occupied });
    const { StatusComponent, wrapperStyle } = visualStates?.[state] ?? {};
    const displaySeatTypeImage = !disableSeatTypeImages && isPlaceSeatTypeImage(place?.type);
    const displayOccupiedSeat = !StatusComponent && !selectedOrClicked && occupied;
    if (displayOccupiedSeat) {
        return (React.createElement("ellipse", { "data-hook": placeId, onClick: enableWholeElementBehavior ? undefined : handlePlaceClick, onMouseEnter: enableWholeElementBehavior ? undefined : onPlaceEnter, onMouseLeave: enableWholeElementBehavior ? undefined : onPlaceLeave, cx: centerX, cy: centerY, rx: OCCUPIED_SEAT_CIRCLE_DIAMETER / 2, ry: OCCUPIED_SEAT_CIRCLE_DIAMETER / 2, style: {
                cursor: 'default',
                fill: isDarkBackground ? COLORS.FILL : OCCUPIED_SEAT_FILL,
                ...wrapperStyle,
            }, stroke: "none" }));
    }
    const getSeatContent = () => {
        if (StatusComponent) {
            return (React.createElement(ReverseRotate, { rotateX: centerX, rotateY: centerY, angle: resolvedAngle, gProps: { pointerEvents: 'none' } },
                React.createElement("svg", { x: x + 3, y: y + 2 },
                    React.createElement(StatusComponent, { ticketColor: hasCategory ? fillColor : SHAPE_STROKE_COLOR }))));
        }
        if (selected) {
            return (React.createElement("g", { transform: resolvedAngle ? `rotate(${reversedRotatedDegrees} ${centerX} ${centerY})` : '', pointerEvents: "none" },
                React.createElement(Confirm, { x: x, y: y, fill: SHAPE_STROKE_COLOR })));
        }
        if (displaySeatTypeImage) {
            return (React.createElement("g", { transform: resolvedAngle ? `rotate(${reversedRotatedDegrees} ${centerX} ${centerY})` : '', className: s.text, pointerEvents: "none" },
                React.createElement(SeatTypeImage, { type: seatType, x: x + 3, y: y + 2, color: textColor, className: "seatTypeIcon" })));
        }
        return (React.createElement("text", { className: classNames('label', s.text), x: centerX, y: centerY, fill: textColor, transform: resolvedAngle ? `rotate(${reversedRotatedDegrees} ${centerX} ${centerY})` : undefined }, label));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("ellipse", { id: placeId, "data-hook": placeId, cx: centerX, cy: centerY, onClick: enableWholeElementBehavior ? undefined : handlePlaceClick, onMouseEnter: enableWholeElementBehavior ? undefined : onPlaceEnter, onMouseLeave: enableWholeElementBehavior ? undefined : onPlaceLeave, rx: radius, ry: radius, className: classNames('seat', s.seat, {
                [s.seatHover]: !wrapperStyle,
                [s.selected]: selectedOrClicked && !wrapperStyle,
                [s.mobile]: mobile,
                [s.wholeTableReservation]: wholeElementReservation,
            }), style: { fill: fillColor, ...wrapperStyle } }),
        getSeatContent()));
};
//# sourceMappingURL=seat.js.map